import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import QRCode from "qrcode";
import { games as gamesApi, formatApiError } from "../lib/api";
import LiveBoard from "../components/LiveBoard";
import { Loader2, Copy, Users, Radio, Home, Play } from "lucide-react";

const SS_KEY = (id) => `canales.game_room.${id}`;

export default function GameHostPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [game, setGame] = useState(null);
  const [room, setRoom] = useState(null);
  const [code, setCode] = useState(null);
  const [hostKey, setHostKey] = useState(null);
  const [teamA, setTeamA] = useState("");
  const [teamB, setTeamB] = useState("");
  const [turnMode, setTurnMode] = useState("strikes");
  const [creating, setCreating] = useState(false);
  const [qr, setQr] = useState("");
  const [soundOn, setSoundOn] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    gamesApi.get(id).then(setGame).catch(() => toast.error(t("games.not_found")));
  }, [id, t]);

  // restore a room from this session (survives refresh)
  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(SS_KEY(id)) || "null");
      if (saved?.code && saved?.hostKey) {
        setCode(saved.code);
        setHostKey(saved.hostKey);
      }
    } catch { /* ignore */ }
  }, [id]);

  const joinUrl = code ? `${window.location.origin}/join/${code}` : "";

  useEffect(() => {
    if (!code) return;
    QRCode.toDataURL(joinUrl, { width: 240, margin: 1, color: { dark: "#0B1220", light: "#FFFFFF" } })
      .then(setQr)
      .catch(() => {});
  }, [code, joinUrl]);

  const fetchRoom = useCallback(async () => {
    if (!code) return;
    try {
      const r = await gamesApi.rooms.get(code, hostKey);
      setRoom(r);
    } catch {
      // room gone
    }
  }, [code, hostKey]);

  useEffect(() => {
    if (!code) return;
    fetchRoom();
    pollRef.current = setInterval(fetchRoom, 1500);
    return () => clearInterval(pollRef.current);
  }, [code, fetchRoom]);

  const createRoom = async () => {
    setCreating(true);
    try {
      const res = await gamesApi.rooms.create({
        game_id: id,
        team_a_name: teamA.trim() || t("games.team_a"),
        team_b_name: teamB.trim() || t("games.team_b"),
        turn_mode: turnMode,
      });
      setCode(res.code);
      setHostKey(res.host_key);
      setRoom(res.room);
      sessionStorage.setItem(SS_KEY(id), JSON.stringify({ code: res.code, hostKey: res.host_key }));
    } catch (e) {
      toast.error(formatApiError(e, t("games.live.create_error")));
    } finally {
      setCreating(false);
    }
  };

  const doStart = async () => {
    try {
      const r = await gamesApi.rooms.start(code, hostKey);
      setRoom(r);
    } catch (e) {
      toast.error(formatApiError(e, t("games.save_error")));
    }
  };

  const act = async (fn) => {
    try {
      const r = await fn();
      setRoom(r);
    } catch (e) {
      toast.error(formatApiError(e, t("games.save_error")));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast.success(t("games.link_copied"));
    } catch {
      toast.message(joinUrl);
    }
  };

  const closeRoom = () => {
    sessionStorage.removeItem(SS_KEY(id));
    navigate("/games");
  };

  const newMatch = () => {
    sessionStorage.removeItem(SS_KEY(id));
    setCode(null);
    setHostKey(null);
    setRoom(null);
    setQr("");
  };

  // ---------- SETUP ----------
  if (!code || !room) {
    return (
      <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center px-6" data-testid="game-host-setup">
        <div className="w-full max-w-md">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FDE68A] flex items-center gap-2">
            <Radio size={14} /> {t("games.live.title")}
          </div>
          <h1 className="font-display text-3xl font-extrabold mt-2">{game?.title || "Canales Dijeron"}</h1>
          <p className="text-slate-400 text-sm mt-1">{t("games.live.setup_hint")}</p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[#3B82F6]">{t("games.live.store_a")}</label>
              <input data-testid="host-team-a" value={teamA} onChange={(e) => setTeamA(e.target.value)} placeholder={t("games.team_a")}
                className="w-full mt-1 bg-[#0F1E3D] border border-[#1E3A8A] rounded-xl px-3 py-2.5 font-bold focus:outline-none focus:ring-2 focus:ring-[#3B82F6]" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[#F59E0B]">{t("games.live.store_b")}</label>
              <input data-testid="host-team-b" value={teamB} onChange={(e) => setTeamB(e.target.value)} placeholder={t("games.team_b")}
                className="w-full mt-1 bg-[#0F1E3D] border border-[#6B3E2E] rounded-xl px-3 py-2.5 font-bold focus:outline-none focus:ring-2 focus:ring-[#F59E0B]" />
            </div>
          </div>

          <div className="mt-4">
            <label className="text-[11px] uppercase tracking-wide text-slate-400">{t("games.live.turn_mode")}</label>
            <div className="mt-2 grid grid-cols-1 gap-2">
              <ModeCard active={turnMode === "strikes"} onClick={() => setTurnMode("strikes")}
                title={t("games.live.mode_strikes")} desc={t("games.live.mode_strikes_desc")} testid="host-mode-strikes" />
              <ModeCard active={turnMode === "instant"} onClick={() => setTurnMode("instant")}
                title={t("games.live.mode_instant")} desc={t("games.live.mode_instant_desc")} testid="host-mode-instant" />
            </div>
          </div>

          <button onClick={createRoom} disabled={creating || !game}
            data-testid="host-create-room"
            className="mt-6 w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-[#0A1329] font-extrabold text-lg py-3.5 rounded-2xl hover:brightness-110 transition-[filter] disabled:opacity-40 inline-flex items-center justify-center gap-2">
            {creating ? <Loader2 size={18} className="animate-spin" /> : <Radio size={18} />}
            {t("games.live.create_room")}
          </button>
          <button onClick={() => navigate("/games")} className="mt-3 w-full text-slate-400 text-sm hover:text-white inline-flex items-center justify-center gap-1">
            <Home size={14} /> {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  // ---------- LOBBY ----------
  if (room.status === "lobby") {
    return (
      <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center px-6 py-10" data-testid="game-host-lobby">
        <div className="w-full max-w-xl text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FDE68A]">{t("games.live.join_at")}</div>
          <div className="mt-2 font-mono text-2xl text-slate-300 break-all">{window.location.host}/join</div>
          <div className="mt-4 text-[11px] uppercase tracking-[0.3em] text-slate-400">{t("games.live.room_code")}</div>
          <div className="font-display text-6xl sm:text-7xl font-black tracking-[0.15em] text-[#F59E0B] mt-1" data-testid="lobby-code">{room.code}</div>

          {qr && (
            <img src={qr} alt="QR" className="mx-auto mt-5 rounded-xl bg-white p-2 w-40 h-40" data-testid="lobby-qr" />
          )}

          <button onClick={copyLink} data-testid="lobby-copy-link"
            className="mt-4 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white border border-white/15 rounded-full px-4 py-2">
            <Copy size={14} /> {t("games.live.copy_join_link")}
          </button>

          <div className="mt-7 grid grid-cols-2 gap-3">
            <JoinSlot name={room.team_a_name} joined={room.team_a_joined} tone="blue" testid="lobby-slot-a" t={t} />
            <JoinSlot name={room.team_b_name} joined={room.team_b_joined} tone="amber" testid="lobby-slot-b" t={t} />
          </div>

          <button onClick={doStart} data-testid="lobby-start"
            className="mt-7 w-full bg-gradient-to-r from-[#10B981] to-[#059669] text-white font-extrabold text-lg py-3.5 rounded-2xl hover:brightness-110 transition-[filter] inline-flex items-center justify-center gap-2">
            <Play size={18} /> {t("games.live.start_match")}
          </button>
          {!(room.team_a_joined && room.team_b_joined) && (
            <p className="text-slate-500 text-xs mt-2">{t("games.live.waiting_teams")}</p>
          )}
          <button onClick={closeRoom} className="mt-3 text-slate-400 text-sm hover:text-white inline-flex items-center justify-center gap-1">
            <Home size={14} /> {t("games.live.close_room")}
          </button>
        </div>
      </div>
    );
  }

  // ---------- PLAYING / FINISHED ----------
  return (
    <LiveBoard
      room={room}
      role="host"
      onNext={() => act(() => gamesApi.rooms.next(code, hostKey))}
      onResetRound={() => act(() => gamesApi.rooms.resetRound(code, hostKey))}
      onSetTurn={(team) => act(() => gamesApi.rooms.setTurn(code, hostKey, team))}
      onEnd={() => act(() => gamesApi.rooms.end(code, hostKey))}
      onExit={closeRoom}
      onNewMatch={newMatch}
      soundOn={soundOn}
      onToggleSound={() => setSoundOn((v) => !v)}
    />
  );
}

function ModeCard({ active, onClick, title, desc, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left rounded-xl border px-4 py-3 transition-colors ${active ? "border-[#F59E0B] bg-[#F59E0B]/10" : "border-[#1E3A8A] bg-[#0F1E3D] hover:border-white/30"}`}>
      <div className="font-bold text-sm">{title}</div>
      <div className="text-xs text-slate-400 mt-0.5">{desc}</div>
    </button>
  );
}

function JoinSlot({ name, joined, tone, testid, t }) {
  const ring = tone === "blue" ? "border-[#1E3A8A]" : "border-[#6B3E2E]";
  return (
    <div className={`rounded-2xl border-2 ${ring} bg-[#0F1E3D] px-4 py-4`} data-testid={testid}>
      <div className="flex items-center justify-center gap-2 text-sm font-bold truncate">
        <Users size={15} /> {name}
      </div>
      <div className={`mt-2 text-xs font-semibold ${joined ? "text-[#10B981]" : "text-slate-500"}`}>
        {joined ? t("games.live.connected") : t("games.live.waiting")}
      </div>
    </div>
  );
}
